import { redirectToLogin } from "@src/appV2/Auth/redirectToLogin";
import axios from "axios";

/**
 * Response-interceptor error handler. Catches 401s centrally and bounces
 * the user to /login. `redirectToLogin` is idempotent and no-ops on login
 * routes, so concurrent failing requests don't loop.
 *
 * Requests whose config has `skipAuthRedirectOnUnauthorized = true` are
 * exempt — those are shortlink/QR-token flows on unauthenticated pages
 * (set by the request interceptor when the caller pre-supplies the
 * `Authorization` header). A 401 there means the caller's token is bad,
 * not that the user's session has expired, so the page should surface its
 * own error instead of getting bounced to /login.
 *
 * The 401 path returns a never-resolving promise rather than re-throwing.
 * Throwing would propagate the rejection to callers — useQuery onError,
 * `meta.logErrorMessage`, ad-hoc `.catch()` handlers — and a generic error
 * toast would flash in the brief window before the browser navigates to
 * /login. Resolving with undefined would crash callers reading
 * `response.data` in that same window. Holding the promise pending keeps
 * the caller in its loading state until React tears down on navigation.
 */
export async function handleApiResponseError(error: unknown): Promise<void> {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    !error.config?.skipAuthRedirectOnUnauthorized
  ) {
    redirectToLogin("apiClient");
    return new Promise<never>(() => {
      // Intentionally never resolves — see comment above.
    });
  }
  if (error instanceof Error) {
    throw error;
  }
}
